import React, { Fragment, memo, useCallback, useContext, useState, useMemo } from 'react'
import { useModalOpen } from 'src/hooks/useModalOpen'
import tw from 'twin.macro'
import { useDispatch } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { CircleCancelMajor, CaretDownMinor, CaretUpMinor } from '@shopify/polaris-icons'
import { GSIcon } from 'src/components/shared/GSIcon'
import { ContractorDetailsModalContext } from '../ContractorDetailsModal.context'
import { ContractorDetails } from 'src/interfaces/contractor'
import { RemoveContractorDeactivationDate } from 'src/constants/actionTypes'
import { colors } from 'src/theme'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { handleApiError } from 'src/utils/errorHandlers'
import { showToast } from 'src/utils/toast'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { MenuActionItem } from 'src/interfaces/ui'
import { SensitiveActionModal } from 'src/components/shared/SensitiveActionModal'
import { SetDeactivationDateModal } from './SetDeactivationDateModal'
import QuestionModalImage from 'src/assets/images/question-modal-img.png'

interface Props {
  contractorDetails: ContractorDetails
  onRefresh(): Promise<void> | void
}

const SetDeactivationDateButtonComponent = ({ contractorDetails, onRefresh }: Props) => {
  const { statusIsUpdating } = useContext(ContractorDetailsModalContext)
  const dispatch = useDispatch()

  const { isOpen: isModalOpen, open: handleOpenModal, close: handleCloseModal } = useModalOpen()
  const {
    isOpen: isRemoveConfirmOpen,
    open: handleOpenRemoveConfirm,
    close: handleCloseRemoveConfirm,
  } = useModalOpen()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const hasDeactivationDate = useMemo(
    () => !!contractorDetails.deactivationScheduledAt,
    [contractorDetails.deactivationScheduledAt],
  )

  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl])

  const calendarIconName = useMemo(
    () => (hasDeactivationDate ? 'gs_calendar_edit' : 'gs_icon_calender_add'),
    [hasDeactivationDate],
  )

  const arrowIcon = useMemo(() => {
    if (!hasDeactivationDate) {
      return undefined
    }

    return isMenuOpen ? (
      <CaretUpMinor width={20} height={20} />
    ) : (
      <CaretDownMinor width={20} height={20} />
    )
  }, [hasDeactivationDate, isMenuOpen])

  const handleButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (hasDeactivationDate) {
        setAnchorEl(event.currentTarget)
      } else {
        handleOpenModal()
      }
    },
    [hasDeactivationDate, handleOpenModal],
  )

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleSetNewDate = useCallback(() => {
    handleCloseMenu()
    handleOpenModal()
  }, [handleCloseMenu, handleOpenModal])

  const handleRemoveDateClick = useCallback(() => {
    handleCloseMenu()
    handleOpenRemoveConfirm()
  }, [handleCloseMenu, handleOpenRemoveConfirm])

  const handleRemoveDate = useCallback(async () => {
    try {
      await createAsyncAction(
        dispatch,
        RemoveContractorDeactivationDate.request({
          partnerDriverId: contractorDetails.id,
        }),
      )
      showToast('Deactivation date has been removed!', { variant: 'success' })
      await onRefresh()
      handleCloseRemoveConfirm()
    } catch (err) {
      handleApiError(err)
    }
  }, [contractorDetails.id, dispatch, onRefresh, handleCloseRemoveConfirm])

  const menuItems = useMemo<MenuActionItem[]>(
    () => [
      {
        label: 'Set new deactivation date',
        icon: <GSIcon iconName="gs_pencil" width={20} height={20} />,
        color: colors.BASICS_BLACK,
        onClick: handleSetNewDate,
      },
      {
        label: 'Remove deactivation date',
        icon: <CircleCancelMajor width={20} height={20} fill={colors.COLORS_RED_500} />,
        color: colors.COLORS_RED_500,
        onClick: handleRemoveDateClick,
      },
    ],
    [handleSetNewDate, handleRemoveDateClick],
  )

  return (
    <Fragment>
      <div css={tw`bg-white`}>
        <Button
          className={tw`px-4`}
          size="small"
          color="grey"
          startIcon={<GSIcon iconName={calendarIconName} width={20} height={20} />}
          endIcon={arrowIcon}
          disabled={statusIsUpdating}
          onClick={handleButtonClick}
        >
          {hasDeactivationDate ? 'Edit date' : 'Set date'}
        </Button>
      </div>
      {hasDeactivationDate && (
        <ActionMenu
          anchorEl={anchorEl}
          items={menuItems}
          isOpen={isMenuOpen}
          itemClassName={tw`min-w-[220px]`}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          onClose={handleCloseMenu}
        />
      )}
      <SensitiveActionModal
        isOpen={isRemoveConfirmOpen}
        title="Deactivation date"
        text="Are you sure you want remove deactivation date?"
        submitLabel="Remove"
        color="red"
        image={
          <img
            src={QuestionModalImage}
            alt="Question"
            css={tw`w-[210px] h-[164px] object-contain`}
          />
        }
        onClose={handleCloseRemoveConfirm}
        onSubmit={handleRemoveDate}
      />
      <SetDeactivationDateModal
        isOpen={isModalOpen}
        contractorId={contractorDetails.id}
        deactivationScheduledAt={contractorDetails.deactivationScheduledAt}
        onClose={handleCloseModal}
        onRefresh={onRefresh}
      />
    </Fragment>
  )
}

export const SetDeactivationDate = memo(SetDeactivationDateButtonComponent)
